import {
  type ColumnDef,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronRight } from "lucide-react";
import { useCallback, useMemo, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx";
import { cn } from "@/lib/utils.ts";
import type { GicsLevel, GicsTreeNode } from "../types.ts";
import { GicsLevelBadge } from "./GicsLevelBadge.tsx";

export interface GicsHierarchyTableProps {
  data: GicsTreeNode[];
  expanded: ExpandedState;
  onExpandedChange: (expanded: ExpandedState) => void;
  onRowClick?: (node: GicsTreeNode) => void;
  selectedId?: string | null;
  selectableLevels?: GicsLevel[];
  showChildrenCount?: boolean;
  emptyMessage?: string;
  className?: string;
}

export function GicsHierarchyTable({
  data,
  expanded,
  onExpandedChange,
  onRowClick,
  selectedId,
  selectableLevels,
  showChildrenCount = false,
  emptyMessage = "No industries found",
  className,
}: GicsHierarchyTableProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const isSelectable = useCallback(
    (level: GicsLevel) => !selectableLevels || selectableLevels.includes(level),
    [selectableLevels],
  );

  const toggleExpand = useCallback(
    (rowId: string) => {
      onExpandedChange({
        ...expanded,
        [rowId]: !expanded[rowId as keyof typeof expanded],
      });
    },
    [expanded, onExpandedChange],
  );

  const columns = useMemo<ColumnDef<GicsTreeNode>[]>(() => {
    const cols: ColumnDef<GicsTreeNode>[] = [
      {
        id: "expand",
        header: () => null,
        size: 40,
        minSize: 40,
        maxSize: 40,
        cell: ({ row }) => {
          const hasChildren =
            row.original.children && row.original.children.length > 0;

          if (!hasChildren) return null;

          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(row.id);
              }}
              className="p-1 hover:bg-muted rounded"
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 transition-transform text-muted-foreground",
                  row.getIsExpanded() && "rotate-90",
                )}
              />
            </button>
          );
        },
      },
      {
        id: "level",
        header: "Level",
        size: 60,
        minSize: 60,
        maxSize: 60,
        cell: ({ row }) => <GicsLevelBadge level={row.original.level} />,
      },
      {
        accessorKey: "code",
        header: "Code",
        size: 100,
        minSize: 100,
        maxSize: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.code}</span>
        ),
      },
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => {
          const isCurrentSelection = selectedId === row.original.id;
          const canSelect = isSelectable(row.original.level);

          return (
            <div
              style={{ paddingLeft: `${row.depth * 20}px` }}
              className="flex items-center gap-2"
            >
              <span
                className={cn(
                  "font-medium",
                  isCurrentSelection && "text-primary",
                )}
              >
                {row.original.name}
              </span>
              {selectableLevels && canSelect && (
                <span className="text-xs text-green-600 dark:text-green-400">
                  (selectable)
                </span>
              )}
            </div>
          );
        },
      },
    ];

    if (showChildrenCount) {
      cols.push({
        id: "children",
        header: "Children",
        size: 80,
        minSize: 80,
        maxSize: 80,
        cell: ({ row }) => {
          if (row.original.level === 4) {
            return <span className="text-xs text-muted-foreground">Leaf</span>;
          }
          return (
            <span className="text-xs text-muted-foreground">
              {row.original.children?.length ?? 0}
            </span>
          );
        },
      });
    }

    return cols;
  }, [
    toggleExpand,
    selectedId,
    isSelectable,
    selectableLevels,
    showChildrenCount,
  ]);

  const table = useReactTable({
    data,
    columns,
    state: { expanded },
    onExpandedChange: onExpandedChange,
    getSubRows: (row) => row.children,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (row) => row.id,
  });

  const handleRowClick = (node: GicsTreeNode) => {
    if (onRowClick && isSelectable(node.level)) {
      onRowClick(node);
    }
  };

  return (
    <div ref={containerRef} className={className}>
      <Table style={{ minWidth: "100%" }}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() }}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center py-8">
                <p className="text-muted-foreground">{emptyMessage}</p>
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => {
              const isCurrentSelection = selectedId === row.original.id;
              const canSelect = isSelectable(row.original.level);
              const isClickable = onRowClick && canSelect;

              return (
                <TableRow
                  key={row.id}
                  data-row-id={row.original.id}
                  onClick={() => handleRowClick(row.original)}
                  className={cn(
                    row.original.level === 1 && "bg-muted/30",
                    row.original.level === 4 && "text-sm",
                    isClickable && "cursor-pointer hover:bg-primary/10",
                    isCurrentSelection && "bg-primary/20",
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export type { ExpandedState };
